/**
 * XSLT-defined XPath functions.
 *
 * XSLT 1.0 section 12 adds functions to the XPath function library, and the
 * EXSLT `node-set()` extension (also under the msxsl namespace) is registered
 * by expanded name next to them, together with the other EXSLT functions of
 * `./exslt/index.js`. They live here rather than in `src/xpath` so
 * that module stays a pure XPath 1.0 implementation; the engine registers this
 * map on its evaluator through
 * {@link XPathEvaluator#registerFunctions}.
 */

"use strict";

import { formatNumber, DEFAULT_DECIMAL_FORMAT } from "./formatNumber.js";
import { isXsltElementAvailable, XSLT_NAMESPACE } from "./elements.js";
import {
  expandedFunctionName,
  resolveNamespacePrefix,
} from "../xpath/evaluator.js";
import { rootNodeOf } from "../xpath/axes.js";
import { EXSLT_COMMON, createExsltFunctions } from "./exslt/index.js";

/** Namespace of the EXSLT common module (`exsl:node-set()`). */
export const EXSLT_COMMON_NAMESPACE = EXSLT_COMMON;

/** Namespace of the MSXML extension functions (`msxsl:node-set()`). */
export const MSXSL_NAMESPACE = "urn:schemas-microsoft-com:xslt";

/** Vendor identification reported by `system-property()`. */
export const VENDOR = "@tradik/xslt-processor";

/** Vendor URL reported by `system-property()`. */
export const VENDOR_URL = "https://github.com/spagu/XSLT-Processor";

/**
 * Values reported by `system-property()`, keyed by property name.
 *
 * The XSLT version is reported as the string `"1"`; XPath 1.0 converts it to
 * the number 1 wherever a numeric comparison or arithmetic is used.
 */
const SYSTEM_PROPERTIES = Object.freeze({
  "xsl:version": "1",
  "xsl:vendor": VENDOR,
  "xsl:vendor-url": VENDOR_URL,
});

/**
 * Get the document that owns a node.
 *
 * @param {Node} node - Any node
 * @returns {Document} The owning document, or the node when it is a document
 */
function ownerDocumentOf(node) {
  return node.ownerDocument || node;
}

/**
 * Convert an evaluated argument to the list of strings it denotes.
 *
 * Node-sets yield the string value of every node, other types yield one string.
 *
 * @param {XPathEvaluator} evaluator - The evaluator providing the conversions
 * @param {(value: *) => string} stringify - The XPath `string()` conversion
 * @param {*} value - An evaluated XPath value
 * @returns {string[]} The string values
 */
function toStringList(evaluator, stringify, value) {
  if (Array.isArray(value)) {
    return value.map((node) => evaluator.getStringValue(node));
  }
  return [stringify(value)];
}

/**
 * Split a QName into its prefix and local part.
 *
 * @param {string} qname - A possibly prefixed name
 * @returns {{prefix: (string|null), localName: string}} The parts of the name
 */
function splitQName(qname) {
  const colon = qname.indexOf(":");
  if (colon === -1) return { prefix: null, localName: qname };
  return {
    prefix: qname.substring(0, colon),
    localName: qname.substring(colon + 1),
  };
}

/**
 * Expanded name key of the decimal format named by the third argument of
 * `format-number()`, with the prefix resolved in the expression's scope
 * (XSLT 1.0 section 12.3); the table is keyed the same way (see
 * declarationNames.js).
 *
 * @param {string} qname - The decimal format QName
 * @param {{namespaces: Object<string, string>}} ctx - The XPath context
 * @returns {string} `{uri}local`, or the local name in no namespace
 * @throws {Error} When the prefix is not declared
 */
function decimalFormatKey(qname, ctx) {
  const { prefix, localName } = splitQName(qname);
  if (prefix === null) return localName;
  const namespaceUri = resolveNamespacePrefix(prefix, ctx.namespaces);
  if (!namespaceUri) {
    throw new Error(
      `format-number(): undeclared namespace prefix "${prefix}" in "${qname}"`,
    );
  }
  return `{${namespaceUri}}${localName}`;
}

/**
 * Build the XSLT function map for an engine.
 *
 * @param {import('./engine.js').XsltEngine} engine - The engine providing loaders, keys and formats
 * @returns {Object<string, Function>} Functions ready for `registerFunctions`
 *
 * @example
 * evaluator.registerFunctions(createXsltFunctions(engine));
 */
export function createXsltFunctions(engine) {
  const evaluator = engine.xpathEvaluator;

  /**
   * The XPath `string()` conversion of the evaluator.
   *
   * `XPathEvaluator#toString` shadows `Object#toString` and takes the value to
   * convert as its argument, so it is bound once under an unambiguous name.
   *
   * @type {(value: *) => string}
   */
  const stringify = evaluator.toString.bind(evaluator);
  const evaluate = (arg, ctx) => evaluator.evaluate(arg, ctx);
  const asString = (arg, ctx) => stringify(evaluate(arg, ctx));

  /**
   * `exsl:node-set(object)` - EXSLT common, also known as `msxsl:node-set()`.
   *
   * A result tree fragment (a DocumentFragment in this engine) becomes a
   * node-set holding its root node, so `exsl:node-set($rtf)/item` selects the
   * fragment's top-level `item` elements. A node-set is returned unchanged; any
   * other value becomes a node-set holding one text node with its string value.
   *
   * @param {Array} args - Argument expressions
   * @param {import('../xpath/evaluator.js').XPathContext} ctx - Evaluation context
   * @returns {Node[]} The node-set
   */
  const nodeSet = (args, ctx) => {
    const value = evaluate(args[0], ctx);
    if (Array.isArray(value)) return value;
    if (value?.nodeType) return [value];
    return [ownerDocumentOf(ctx.node).createTextNode(stringify(value))];
  };

  return {
    ...createExsltFunctions(engine),
    [expandedFunctionName(EXSLT_COMMON_NAMESPACE, "node-set")]: nodeSet,
    [expandedFunctionName(MSXSL_NAMESPACE, "node-set")]: nodeSet,

    /**
     * `document(object, base?)` - load external XML documents.
     *
     * An empty URI denotes the stylesheet itself. Without a document loader, or
     * when the loader returns null, the result is an empty node-set. The
     * optional second argument is read as a base URI string.
     */
    document: (args, ctx) => {
      const baseUri = args.length > 1 ? asString(args[1], ctx) : engine.baseUri;
      const uris = toStringList(evaluator, stringify, evaluate(args[0], ctx));
      const result = [];

      for (const uri of uris) {
        const doc = engine.loadDocument(uri, baseUri || engine.baseUri);
        if (doc && !result.includes(doc)) result.push(doc);
      }

      return result;
    },

    /**
     * `key(name, value)` - look up nodes through an `xsl:key` index of the
     * tree containing the context node: its document, or the fragment of a
     * result tree fragment converted with `exsl:node-set()`.
     */
    key: (args, ctx) => {
      const name = asString(args[0], ctx);
      const values = toStringList(evaluator, stringify, evaluate(args[1], ctx));
      return engine.keyRegistry.lookup(name, values, rootNodeOf(ctx.node));
    },

    /** `format-number(number, pattern, decimalFormat?)`. */
    "format-number": (args, ctx) => {
      const value = evaluator.toNumber(evaluate(args[0], ctx));
      const pattern = asString(args[1], ctx);
      const formatName =
        args.length > 2 ? decimalFormatKey(asString(args[2], ctx), ctx) : "";
      const format =
        engine.decimalFormats[formatName] || DEFAULT_DECIMAL_FORMAT;
      return formatNumber(value, pattern, format);
    },

    /** `current()` - the XSLT current node, not the XPath context node. */
    current: (args, ctx) => {
      const currentNode = ctx.hostContext?.currentNode;
      return currentNode ? [currentNode] : [ctx.node];
    },

    /** `generate-id(node-set?)` - a stable id for the life of the transform. */
    "generate-id": (args, ctx) => {
      let node = ctx.node;

      if (args.length > 0) {
        const nodeSet = evaluate(args[0], ctx);
        node = Array.isArray(nodeSet) ? nodeSet[0] : nodeSet;
      }

      return node ? engine.generateId(node) : "";
    },

    /** `system-property(name)` - XSLT version and vendor information. */
    "system-property": (args, ctx) => {
      const name = asString(args[0], ctx);
      return Object.hasOwn(SYSTEM_PROPERTIES, name)
        ? SYSTEM_PROPERTIES[name]
        : "";
    },

    /**
     * `function-available(name)` - reflects the evaluator function table. A
     * prefixed name is resolved through the stylesheet's namespace bindings,
     * just as a call of that function would be. A function can report itself
     * unavailable through an `isAvailable()` property (`dyn:evaluate()` does
     * until the engine enables it).
     */
    "function-available": (args, ctx) => {
      const { prefix, localName } = splitQName(asString(args[0], ctx));
      const fn = evaluator.resolveFunction(localName, prefix, ctx.namespaces);
      return fn !== null && fn.isAvailable?.() !== false;
    },

    /** `element-available(name)` - reflects the XSLT elements the engine runs. */
    "element-available": (args, ctx) => {
      const { prefix, localName } = splitQName(asString(args[0], ctx));
      // An unprefixed name is in the default namespace (XSLT 1.0 section
      // 15), which may be the XSLT namespace itself (libxslt bug-200)
      const namespaceUri = prefix
        ? (ctx.namespaces[prefix] ?? (prefix === "xsl" ? XSLT_NAMESPACE : null))
        : ctx.namespaces[""] || null;
      if (!namespaceUri) return false;

      if (namespaceUri === XSLT_NAMESPACE) {
        return isXsltElementAvailable(localName);
      }
      // Extension elements with an implementation (registerExtensionElement)
      return Boolean(
        engine.extensionElements?.has(`{${namespaceUri}}${localName}`),
      );
    },

    /**
     * `unparsed-entity-uri(name)` - always empty.
     *
     * Unparsed entity declarations are not exposed by the DOM, so this
     * processor cannot resolve them; returning the empty string keeps
     * stylesheets that call the function working.
     */
    "unparsed-entity-uri": () => "",
  };
}
